import { CheckRounded, DescriptionOutlined } from '@mui/icons-material'
import { Box, Button } from '@mui/material'
import { FC } from 'react'
import { UseMutationResult } from 'react-query'
import { AdoptionAttemptDto, AnimalInsightsDtoType } from '../../../interactors/gen/backendClient'
import { getAdoptionAttemptDocumentsSrc } from '../../../utils/S3-links'
import { InfoBox } from '../../common/InfoBox'
import { RefuseFileModal } from './RefuseFileModal'

interface Props {
  attempt: AdoptionAttemptDto
  setAdoptionAttempt: React.Dispatch<React.SetStateAction<AdoptionAttemptDto | undefined>>
  updateAttemptMutation: UseMutationResult<AdoptionAttemptDto, unknown, AdoptionAttemptDto, unknown>
  adopterId: string
  disabled?: boolean
}

export const AllAdoptionInsightsInfoBox: FC<Props> = ({
  attempt,
  setAdoptionAttempt,
  updateAttemptMutation,
  adopterId,
  disabled = false,
}) => {
  const order: Array<AnimalInsightsDtoType> = ['info', 'error', 'warning']

  const renderValidationActions = (type: 'certificate' | 'contract', fileKey?: string) => {
    if (!fileKey) return undefined

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          pt: { xs: 0.1, sm: 0 },
        }}
      >
        <Button
          component="a"
          href={getAdoptionAttemptDocumentsSrc(fileKey)}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          sx={{
            minWidth: 0,
            width: 30,
            height: 30,
            borderRadius: 2,
            color: '#203864',
            borderColor: 'rgba(105, 141, 172, 0.24)',
            backgroundColor: 'rgba(255,255,255,0.72)',
            px: 0,
            '& .MuiSvgIcon-root': { fontSize: 16 },
          }}
        >
          <DescriptionOutlined />
        </Button>
        <RefuseFileModal
          adopterId={adopterId}
          adoptionAttempt={attempt}
          type={type}
          setAdoptionAttempt={setAdoptionAttempt}
          disabled={disabled}
          iconOnly
        />
        <Button
          variant="outlined"
          sx={{
            minWidth: 0,
            width: 30,
            height: 30,
            borderRadius: 2,
            px: 0,
            color: '#2B6CB0',
            borderColor: 'rgba(43, 108, 176, 0.24)',
            backgroundColor: 'rgba(255,255,255,0.72)',
            '& .MuiSvgIcon-root': { fontSize: 16 },
            '&:hover': {
              borderColor: 'rgba(43, 108, 176, 0.34)',
              backgroundColor: 'rgba(245, 249, 255, 0.96)',
            },
          }}
          onClick={() =>
            updateAttemptMutation.mutate({
              ...attempt,
              [type]: { ...attempt[type], validated: true },
            })
          }
          disabled={disabled}
        >
          <CheckRounded />
        </Button>
      </Box>
    )
  }

  const isContractToValidate = Boolean(attempt.contract?.signed && !attempt.contract?.validated)
  const isCertificateToValidate = Boolean(attempt.certificate?.signed && !attempt.certificate?.validated)

  return (
    <Box sx={{ marginBottom: 1, marginTop: 1.5 }}>
      {attempt.insights
        .sort((a, b) => {
          const typeA = a.type
          const typeB = b.type
          return order.indexOf(typeA) - order.indexOf(typeB)
        })
        .map((insight) => {
          const lowerMessage = insight.message.toLowerCase()
          const contractActions = lowerMessage.includes('contrat') && isContractToValidate
          const certificateActions = lowerMessage.includes('certificat') && isCertificateToValidate
          const hasDocumentValidationActions = contractActions || certificateActions

          return (
            <InfoBox
              key={`${insight.type}-${insight.message}`}
              messageType={hasDocumentValidationActions ? 'success' : insight.type}
              content={insight.message}
              sx={
                hasDocumentValidationActions
                  ? {
                      marginBottom: 1,
                      background: 'rgba(234, 246, 237, 0.96)',
                      borderColor: 'rgba(88, 140, 99, 0.16)',
                    }
                  : { marginBottom: 1 }
              }
              actions={
                contractActions
                  ? renderValidationActions('contract', attempt.contract?.signed)
                  : certificateActions
                  ? renderValidationActions('certificate', attempt.certificate?.signed)
                  : undefined
              }
            />
          )
        })}
    </Box>
  )
}
